import React, { VFC, useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { Box, Grid, makeStyles, FileItem, Alert } from '@dxb-web-ui-framework/design-system';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { transactionService } from '../../../../../../channels/secure-web/services';
import { useSelectedCustomerId } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { TransactionDocument } from '../../../../../transaction/src/domain/Transaction/entities/TransactionDocuments';
import { ConsolidatedTransactionDetails } from '../../../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { SkeletonWrapperList } from '../Skeleton';
import { TransactionDoc, TransactionUploader } from './';

const useStyles = makeStyles(theme => ({
  retry: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  fileItemText: {
    '& p:first-of-type': {
      'overflow-wrap': 'anywhere',
    },
    maxHeight: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'unset',
      padding: `${theme.spacing(0)}px ${theme.spacing(3)}px ${theme.spacing(3)}px`,
    },
  },
}));

type TransactionDocState = {
  orderId: number;
  meta: {
    timestamp: string;
  };
};

export type TransactionDocumentsProps = {
  transactionDetails: ConsolidatedTransactionDetails;
  isStandalonePage: boolean;
  shouldHideDeleteButton?: boolean;
};

export type DefaultTransactionDocumentsState = {
  documents?: Nullable<TransactionDocument[]>;
  isLoading: boolean;
  error?: AxiosError<APIGatewayResponse<Nullable<TransactionDocument[]>>, any>;
};

const TransactionDocuments: VFC<TransactionDocumentsProps> = ({
  transactionDetails,
  isStandalonePage,
  shouldHideDeleteButton,
}) => {
  const [transactionsDoc, setTransactionsDoc] = useState<Nullable<TransactionDocState>>({
    orderId: transactionDetails.orderId,
    meta: { timestamp: new Date().toISOString() },
  });
  const classes = useStyles();
  const { t } = useTranslation();
  const customerId = useSelectedCustomerId();

  const {
    data: transactionDocuments,
    error: transactionDocumentsError,
    isLoading: isLoadingTransactionDocuments,
  } = useInvokeSWRService<TransactionDocument[]>(
    transactionService.GetTransactionDocuments,
    {
      customerId: Number(customerId),
      orderId: transactionDetails.orderId,
      meta: transactionsDoc?.meta,
    },
    Boolean(transactionsDoc),
  );

  const fetchTransactionDocuments = () => {
    setTransactionsDoc({ orderId: transactionDetails.orderId, meta: { timestamp: new Date().toISOString() } });
  };

  return (
    <Grid
      container
      item
      xs={isStandalonePage ? 12 : 4}
      justifyContent='center'
      alignContent='center'
      className={classes.container}>
      {!isEmpty(transactionDocumentsError) && (
        <Alert
          icon={false}
          severity='error'
          variant='filled'
          action={
            <Box
              title={t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Retry')}
              onClick={fetchTransactionDocuments}
              className={classes.retry}>
              {t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Retry')}
            </Box>
          }>
          {t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Error.Message')}
        </Alert>
      )}
      <Box className={classes.fileItemText}>
        <TransactionUploader
          customerId={customerId}
          transactionDetails={transactionDetails}
          isLoadingTransactionDocuments={isLoadingTransactionDocuments}
          onRefetchData={fetchTransactionDocuments}
        />
        {isEmpty(transactionDocumentsError) &&
          !isLoadingTransactionDocuments &&
          transactionDocuments?.map(({ fileName, fileSn, referenceNumber }) => (
            <Box key={fileName + fileName} mt={0.5}>
              <TransactionDoc
                transactionService={transactionService}
                customerId={customerId}
                fileName={fileName}
                fileSn={fileSn}
                referenceNumber={referenceNumber}
                onRefetchData={fetchTransactionDocuments}
                shouldHideDeleteButton={shouldHideDeleteButton}
              />
            </Box>
          ))}
        {isLoadingTransactionDocuments && (
          <SkeletonWrapperList
            skeletonsToDisplay={2}
            Component={FileItem}
            boxProps={{ padding: 0, width: '100%' }}
            paperProps={{ elevation: 0 }}
            componentProps={{ hasSkeleton: true }}
          />
        )}
      </Box>
    </Grid>
  );
};

export { TransactionDocuments };
